/**
 * Period/Previous filter catalog + formatting, shared between UsageView.tsx and
 * AggReportView.tsx (whenever a saved Aggregate report includes the 'period'/'previous'
 * filters — see aggReportConstants.ts's AGG_FILTER_CATALOG). Extracted from UsageView.tsx
 * verbatim; no behavior change.
 */

export interface PeriodOption {
  value: string;
  label: string;
}

export const PERIOD_OPTIONS: PeriodOption[] = [
  { value: '1', label: 'Yearly' },
  { value: '2', label: 'Quarterly' },
  { value: '3', label: 'Monthly' },
  { value: '4', label: 'Weekly' },
  { value: '5', label: 'Daily' },
];

// Singular unit name per period type — used to label the Previous dropdown ("1 month",
// "2 months", ...) so its options read in the same units as the selected Period.
export const PERIOD_UNIT_LABEL: Record<string, string> = {
  '1': 'year',
  '2': 'quarter',
  '3': 'month',
  '4': 'week',
  '5': 'day',
};

export const pluralizeUnit = (count: number, unit: string): string => `${count} ${unit}${count === 1 ? '' : 's'}`;

export const PREVIOUS_VALUES: string[] = Array.from({ length: 30 }, (_, index) => String(index + 1));

export const DEFAULT_PERIOD = '3';
export const DEFAULT_PREVIOUS = '4';

const pad2 = (value: number): string => String(value).padStart(2, '0');

// Period values from stats_usage_get / stats_agg_get are a count of period-units elapsed since
// 1/1/2000 — this converts that count back to the calendar date the period starts on, formatted
// to the precision that actually varies for the selected period type (a year for Yearly,
// year-month for Quarterly/Monthly since they share month granularity, year-month-day for
// Weekly/Daily).
export const formatPeriodLabel = (periodType: string, rawPeriod: string): string => {
  const units = parseInt(rawPeriod, 10);
  if (Number.isNaN(units)) {
    return rawPeriod;
  }

  const date = new Date(Date.UTC(2000, 0, 1));

  switch (periodType) {
    case '1': // Yearly
      date.setUTCFullYear(date.getUTCFullYear() + units);
      return String(date.getUTCFullYear());
    case '2': // Quarterly
      date.setUTCMonth(date.getUTCMonth() + units * 3);
      return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}`;
    case '3': // Monthly
      date.setUTCMonth(date.getUTCMonth() + units);
      return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}`;
    case '4': // Weekly
      date.setUTCDate(date.getUTCDate() + units * 7);
      return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
    case '5': // Daily
      date.setUTCDate(date.getUTCDate() + units);
      return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
    default:
      return rawPeriod;
  }
};
